import {computed, Injectable, signal} from '@angular/core';
import {
  createInitialPolarisState,
  DEFAULT_POLARIS_TRANSIENT_INDEX,
  PolarisAccountKey,
  PolarisChatAggregate,
  PolarisChatId,
  PolarisChatOverview,
  PolarisMessageId,
  PolarisOptimisticSendInput,
  PolarisPaginationParams,
  PolarisPaginationState,
  PolarisRepositoryConversationHistoryMessage,
  PolarisRepositoryState,
  PolarisSendReconcileInput,
  PolarisTransientIndex,
  PolarisTurn
} from './model';
import {sortChatOrderByRecent} from './polaris.utils';

@Injectable({
  providedIn: 'root'
})
export class PolarisStateService {
  private readonly _state = signal<PolarisRepositoryState>(createInitialPolarisState());
  private readonly _transientIndex = signal<PolarisTransientIndex>({...DEFAULT_POLARIS_TRANSIENT_INDEX});

  public readonly state = computed(() => this._state());
  public readonly accountKey = computed(() => this._state().accountKey);
  public readonly activeChatId = computed(() => this._state().activeChatId);
  public readonly recentChatId = computed(() => this._state().recentChatId);
  public readonly recentChatIdTimeout = computed(() => this._state().recentChatIdTimeout);
  public readonly pagination = computed(() => this._state().pagination);
  public readonly loading = computed(() => this._state().loading);
  public readonly errors = computed(() => this._state().errors);
  public readonly migration = computed(() => this._state().migration);

  public readonly chatList = computed<PolarisChatAggregate[]>(() => {
    const state = this._state();
    return state.chatOrder
      .map(chatId => state.chatsById[chatId])
      .filter((chat): chat is PolarisChatAggregate => !!chat);
  });

  public readonly activeChat = computed<PolarisChatAggregate | null>(() => {
    const state = this._state();
    if (!state.activeChatId) {
      return null;
    }
    return state.chatsById[state.activeChatId] ?? null;
  });

  public readonly activeChatTurns = computed<PolarisTurn[]>(() => {
    return this.activeChat()?.turns ?? [];
  });

  public readonly canLoadMoreChats = computed(() => {
    const state = this._state();
    return state.chatOrder.length < state.pagination.total;
  });

  public initialize(accountKey: PolarisAccountKey): void {
    this._state.set({
      ...createInitialPolarisState(),
      accountKey
    });
    this._transientIndex.set({...DEFAULT_POLARIS_TRANSIENT_INDEX});
  }

  public reset(): void {
    this._state.set(createInitialPolarisState());
    this._transientIndex.set({...DEFAULT_POLARIS_TRANSIENT_INDEX});
  }

  public setActiveChat(chatId: PolarisChatId | null): void {
    this.patchState({activeChatId: chatId});
  }

  public setOverviewLoading(isLoading: boolean): void {
    this.patchLoading({overview: isLoading});
  }

  public setActiveChatLoading(isLoading: boolean): void {
    this.patchLoading({activeChat: isLoading});
  }

  public setSendLoading(isLoading: boolean): void {
    if (isLoading) {
      this.patchLoading({sendMessage: true});
      return;
    }
    this.reconcileSendLoading();
  }

  public setMigrationLoading(isLoading: boolean): void {
    this.patchLoading({migration: isLoading});
  }

  public setOverviewError(error: string | null): void {
    this.patchErrors({overview: error});
  }

  public setActiveChatError(error: string | null): void {
    this.patchErrors({activeChat: error});
  }

  public setSendError(error: string | null): void {
    this.patchErrors({sendMessage: error});
  }

  public setMigrationError(error: string | null): void {
    this.patchErrors({migration: error});
  }

  public setPagination(pagination: PolarisPaginationState): void {
    this.patchState({pagination});
  }

  public getChatByIdQueryParams(overrides: PolarisPaginationParams = {}): PolarisPaginationParams {
    const pagination = this._state().pagination;
    return {
      all: true,
      limit: pagination.limit,
      offset: pagination.offset,
      ...overrides
    };
  }

  public isChatHydrated(chatId: PolarisChatId): boolean {
    return !!this._state().hydratedChatIds[chatId];
  }

  public upsertOverviewItems(overviews: PolarisChatOverview[]): void {
    const state = this._state();
    const updatedChatsById = {...state.chatsById};
    const appendedOrder: string[] = [];

    for (const overview of overviews) {
      const existing = updatedChatsById[overview.chatId];
      updatedChatsById[overview.chatId] = {
        overview,
        turns: existing?.turns ?? [],
        isThinking: existing?.isThinking ?? false
      };
      if (!state.chatOrder.includes(overview.chatId)) {
        appendedOrder.push(overview.chatId);
      }
    }

    this._state.set({
      ...state,
      chatsById: updatedChatsById,
      chatOrder: sortChatOrderByRecent([...state.chatOrder, ...appendedOrder], updatedChatsById)
    });
  }

  public upsertChat(chat: PolarisChatAggregate): void {
    const state = this._state();
    const chatsById = {
      ...state.chatsById,
      [chat.overview.chatId]: chat
    };
    const chatOrder = state.chatOrder.includes(chat.overview.chatId)
      ? state.chatOrder
      : [...state.chatOrder, chat.overview.chatId];

    this._state.set({
      ...state,
      chatsById,
      hydratedChatIds: {
        ...state.hydratedChatIds,
        [chat.overview.chatId]: true
      },
      chatOrder: sortChatOrderByRecent(chatOrder, chatsById)
    });
  }

  public appendOptimisticTurn(input: PolarisOptimisticSendInput): void {
    const state = this._state();
    const chat = state.chatsById[input.chatId];
    if (!chat) {
      return;
    }

    const nowIso = new Date(input.timestamp).toISOString();
    const optimisticTurn: PolarisTurn = {
      id: input.interactionId,
      chatId: input.chatId,
      status: 'pending',
      origin: input.origin ?? input.widgetContext?.origin,
      widgetName: input.widgetName ?? input.widgetContext?.name,
      widgetContext: input.widgetContext,
      myBrand: input.myBrand,
      myCompetitors: input.myCompetitors ?? [],
      messagePair: {
        id: input.requestId,
        chatId: input.chatId,
        userPrompt: input.userPrompt,
        response: '',
        status: 'pending',
        userTimestamp: input.timestamp,
        systemTimestamp: null,
        followUpQuestions: [],
        insights: null,
        creativesURLs: [],
        graphs: [],
        requestId: input.requestId,
        interactionId: input.interactionId,
        polarisLambdaRequestId: null
      }
    };

    const updatedChat: PolarisChatAggregate = {
      ...chat,
      overview: {
        ...chat.overview,
        lastModified: nowIso,
        lastMessageTime: nowIso
      },
      turns: [...chat.turns, optimisticTurn],
      isThinking: true
    };

    this._transientIndex.update(index => ({
      ...index,
      requestToPendingMessageId: {
        ...index.requestToPendingMessageId,
        [input.requestId]: optimisticTurn.messagePair.id
      },
      interactionToRequestId: {
        ...index.interactionToRequestId,
        [input.interactionId]: input.requestId
      }
    }));

    this.upsertChat(updatedChat);
    this.reconcileSendLoading();
  }

  public reconcileSendSuccess(input: PolarisSendReconcileInput): void {
    const state = this._state();
    const existingChat = state.chatsById[input.chat.chatId];
    const existingTurns = existingChat?.turns ?? [];

    const interactionId = input.turn.messagePair.interactionId;
    const requestId = input.turn.messagePair.requestId;
    const normalizedTurn: PolarisTurn = {
      ...input.turn,
      messagePair: {
        ...input.turn.messagePair,
        error: undefined
      }
    };

    const interactionMatchIndex = interactionId
      ? existingTurns.findIndex(turn => turn.messagePair.interactionId === interactionId)
      : -1;
    const requestMatchIndex = interactionMatchIndex === -1 && requestId
      ? existingTurns.findIndex(turn => turn.messagePair.requestId === requestId)
      : -1;
    const replaceIndex = interactionMatchIndex !== -1 ? interactionMatchIndex : requestMatchIndex;
    const existingTurn = replaceIndex !== -1 ? existingTurns[replaceIndex] : undefined;

    const mergedTurns = replaceIndex !== -1
      ? existingTurns.map((turn, index) => (index === replaceIndex ? this.mergeTurnMetadata(existingTurn, normalizedTurn) : turn))
      : [...existingTurns, normalizedTurn];

    const mergedChat: PolarisChatAggregate = {
      overview: input.chat,
      turns: mergedTurns,
      isThinking: mergedTurns.some(turn => turn.status === 'pending'),
      isNewlyCreated: false
    };

    this.upsertChat(mergedChat);
    this.clearSendTransientByCorrelation({
      interactionId: interactionId ?? undefined,
      requestId: requestId ?? undefined
    });
    this.patchErrors({sendMessage: null});
    this.reconcileSendLoading();
  }

  private mergeTurnMetadata(existingTurn: PolarisTurn | undefined, nextTurn: PolarisTurn): PolarisTurn {
    if (!existingTurn) {
      return nextTurn;
    }
    return {
      ...nextTurn,
      origin: nextTurn.origin ?? existingTurn.origin,
      widgetName: nextTurn.widgetName ?? existingTurn.widgetName,
      widgetContext: nextTurn.widgetContext ?? existingTurn.widgetContext,
      myBrand: nextTurn.myBrand ?? existingTurn.myBrand,
      myCompetitors: nextTurn.myCompetitors ?? existingTurn.myCompetitors
    };
  }

  public markTurnFailed(chatId: PolarisChatId, interactionId: string | undefined, error: string): void {
    const state = this._state();
    const chat = state.chatsById[chatId];
    if (!chat) {
      return;
    }
    const requestId = interactionId ? this.getRequestIdForInteraction(interactionId) ?? undefined : undefined;

    let failedTurnIndex = interactionId
      ? chat.turns.findIndex(turn => turn.status === 'pending' && turn.messagePair.interactionId === interactionId)
      : -1;

    if (failedTurnIndex === -1 && requestId) {
      failedTurnIndex = chat.turns.findIndex(turn => turn.status === 'pending' && turn.messagePair.requestId === requestId);
    }

    // Fallback: if ID-based match misses, fail the latest pending turn.
    if (failedTurnIndex === -1) {
      for (let index = chat.turns.length - 1; index >= 0; index -= 1) {
        if (chat.turns[index].status === 'pending') {
          failedTurnIndex = index;
          break;
        }
      }
    }
    // No pending turn exists; keep flags/indices consistent and exit.
    if (failedTurnIndex === -1) {
      this.clearSendTransientByCorrelation({interactionId, requestId});
      this.patchErrors({sendMessage: error});
      this.reconcileSendLoading();
      return;
    }

    const turns: PolarisTurn[] = chat.turns.map((turn, index) => {
      if (index !== failedTurnIndex) {
        return turn;
      }
      return {
        ...turn,
        status: 'failed' as const,
        messagePair: {
          ...turn.messagePair,
          status: 'failed',
          error
        }
      };
    });
    const failedTurn = turns[failedTurnIndex];

    this.upsertChat({
      ...chat,
      turns,
      isThinking: turns.some(turn => turn.status === 'pending'),
      isNewlyCreated: false
    });
    this.clearSendTransientByCorrelation({
      interactionId: failedTurn.messagePair.interactionId ?? interactionId,
      requestId: failedTurn.messagePair.requestId ?? requestId
    });
    this.patchErrors({sendMessage: error});
    this.reconcileSendLoading();
  }

  public getRequestIdForInteraction(interactionId: string): string | null {
    return this._transientIndex().interactionToRequestId[interactionId] ?? null;
  }

  public getPendingMessageIdForRequest(requestId: string): PolarisMessageId | null {
    return this._transientIndex().requestToPendingMessageId[requestId] ?? null;
  }

  public isInteractionPending(interactionId: string): boolean {
    return !!this.getRequestIdForInteraction(interactionId);
  }

  public clearSendTransientByCorrelation(input: {interactionId?: string; requestId?: string}): void {
    const currentIndex = this._transientIndex();
    const resolvedRequestId = input.requestId
      ?? (input.interactionId ? currentIndex.interactionToRequestId[input.interactionId] : undefined);

    const requestToPendingMessageId = {...currentIndex.requestToPendingMessageId};
    const interactionToRequestId = {...currentIndex.interactionToRequestId};

    if (resolvedRequestId) {
      delete requestToPendingMessageId[resolvedRequestId];
    }
    if (input.interactionId) {
      delete interactionToRequestId[input.interactionId];
    }

    this._transientIndex.set({
      requestToPendingMessageId,
      interactionToRequestId
    });
  }

  public buildConversationHistory(
    chatId: PolarisChatId,
    excludeInteractionId?: string
  ): PolarisRepositoryConversationHistoryMessage[] {
    const chat = this._state().chatsById[chatId];
    if (!chat) {
      return [];
    }

    return chat.turns
      .filter(turn => turn.status !== 'failed')
      .filter(turn => (excludeInteractionId ? turn.messagePair.interactionId !== excludeInteractionId : true))
      .flatMap(turn => {
        const assistantResponse = turn.messagePair.response.trim();
        const history: PolarisRepositoryConversationHistoryMessage[] = [
          {role: 'user', content: turn.messagePair.userPrompt}
        ];
        if (assistantResponse) {
          history.push({role: 'assistant', content: assistantResponse});
        }
        return history;
      });
  }

  public getChatById(chatId: PolarisChatId): PolarisChatAggregate | undefined {
    return this._state().chatsById[chatId];
  }

  public setChatTitle(chatId: PolarisChatId, title: string): void {
    const chat = this._state().chatsById[chatId];
    if (chat) {
      this.upsertChat({
        ...chat,
        overview: {...chat.overview, title}
      });
    }
  }

  public setRecentChatId(chatId: PolarisChatId | null): void {
    this.patchState({recentChatId: chatId});
  }

  public setRecentChatIdTimeout(timeout: NodeJS.Timeout | null): void {
    this.patchState({recentChatIdTimeout: timeout});
  }

  private patchState(patch: Partial<PolarisRepositoryState>): void {
    this._state.update(state => ({...state, ...patch}));
  }

  private patchLoading(patch: Partial<PolarisRepositoryState['loading']>): void {
    this._state.update(state => ({
      ...state,
      loading: {
        ...state.loading,
        ...patch
      }
    }));
  }

  private patchErrors(patch: Partial<PolarisRepositoryState['errors']>): void {
    this._state.update(state => ({
      ...state,
      errors: {
        ...state.errors,
        ...patch
      }
    }));
  }

  private reconcileSendLoading(): void {
    const hasPendingTurns = Object.values(this._state().chatsById)
      .some(chat => chat.turns.some(turn => turn.status === 'pending'));
    const hasPendingTransientEntries = Object.keys(this._transientIndex().interactionToRequestId).length > 0;
    this.patchLoading({sendMessage: hasPendingTurns || hasPendingTransientEntries});
  }

}
